/** SCAPI attribute id for the event/course date facet (BM: eventDateFacet). */
export const DATE_FACET_ATTRIBUTE_ID = 'c_eventDateFacet';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const RANGE_RE = /\((\d{8})\.\.(\d{8})\)/;

export type DateRange = {
    from: Date;
    to: Date;
};

/** Local calendar date as YYYYMMDD string (e.g. "20260803"). */
export function toYyyymmdd(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
}

/** Parse a YYYYMMDD string into a local Date at midnight. Returns null if invalid. */
export function fromYyyymmdd(value: string): Date | null {
    if (!/^\d{8}$/.test(value)) {
        return null;
    }
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(4, 6));
    const day = Number(value.slice(6, 8));
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }
    return date;
}

function startOfLocalDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function minMaxRange(dates: Date[]): DateRange | null {
    if (dates.length === 0) {
        return null;
    }
    let from = dates[0];
    let to = dates[0];
    for (const date of dates) {
        if (date.getTime() < from.getTime()) {
            from = date;
        }
        if (date.getTime() > to.getTime()) {
            to = date;
        }
    }
    return { from, to };
}

/** Inclusive list of YYYYMMDD values for each day in [from, to]. */
export function expandDateRangeToRefineValues(from: Date, to: Date): string[] {
    const start = startOfLocalDay(from);
    const end = startOfLocalDay(to);
    const [lo, hi] = start.getTime() <= end.getTime() ? [start, end] : [end, start];
    const days: string[] = [];
    for (let cursor = lo.getTime(); cursor <= hi.getTime(); cursor += MS_PER_DAY) {
        days.push(toYyyymmdd(new Date(cursor)));
    }
    return days;
}

/** SCAPI range refine value, inclusive on both ends (e.g. "(20260809..20260811)"). */
export function formatDateFacetRefineValue(from: Date, to: Date): string {
    const start = startOfLocalDay(from);
    const end = startOfLocalDay(to);
    const [lo, hi] = start.getTime() <= end.getTime() ? [start, end] : [end, start];
    return `(${toYyyymmdd(lo)}..${toYyyymmdd(hi)})`;
}

function collectDatesFromRefineValue(value: string): Date[] {
    const rangeMatch = value.match(RANGE_RE);
    if (rangeMatch) {
        const dates: Date[] = [];
        const from = fromYyyymmdd(rangeMatch[1]);
        const to = fromYyyymmdd(rangeMatch[2]);
        if (from) {
            dates.push(from);
        }
        if (to) {
            dates.push(to);
        }
        return dates;
    }
    const dates: Date[] = [];
    for (const token of value.split('|')) {
        const parsed = fromYyyymmdd(token);
        if (parsed) {
            dates.push(parsed);
        }
    }
    return dates;
}

/** Min/max of existing `c_eventDateFacet=*` refine pairs as a Date range. */
export function parseDateRangeFromRefines(refines: string[]): DateRange | null {
    const prefix = `${DATE_FACET_ATTRIBUTE_ID}=`;
    const dates: Date[] = [];
    for (const refine of refines) {
        if (!refine.startsWith(prefix)) {
            continue;
        }
        dates.push(...collectDatesFromRefineValue(refine.slice(prefix.length)));
    }
    return minMaxRange(dates);
}

/**
 * Strip existing date-facet refine pairs, then optionally append a single range value.
 * Pass `null` to clear the date facet only.
 */
export function replaceDateFacetRefines(currentRefines: string[], rangeString: string | null): string[] {
    const prefix = `${DATE_FACET_ATTRIBUTE_ID}=`;
    const withoutDate = currentRefines.filter((r) => !r.startsWith(prefix));
    if (!rangeString) {
        return withoutDate;
    }
    return [...withoutDate, `${DATE_FACET_ATTRIBUTE_ID}=${rangeString}`];
}

/**
 * Collapse discrete / pipe-separated date-facet values into one `(from..to)` pair
 * so productSearch stays under the 512-character refine limit.
 */
export function normalizeDateFacetRefines(refines: string[]): string[] {
    const range = parseDateRangeFromRefines(refines);
    if (!range) {
        return refines;
    }
    return replaceDateFacetRefines(refines, formatDateFacetRefineValue(range.from, range.to));
}

/**
 * Saturday–Sunday of the current weekend if `today` is Sat/Sun,
 * otherwise the next Saturday–Sunday.
 */
export function getThisWeekendRange(today: Date): DateRange {
    const start = startOfLocalDay(today);
    const dayOfWeek = start.getDay(); // 0=Sun … 6=Sat
    let daysUntilSaturday: number;
    if (dayOfWeek === 6) {
        daysUntilSaturday = 0;
    } else if (dayOfWeek === 0) {
        daysUntilSaturday = -1;
    } else {
        daysUntilSaturday = 6 - dayOfWeek;
    }
    const saturday = new Date(start.getTime() + daysUntilSaturday * MS_PER_DAY);
    const sunday = new Date(saturday.getTime() + MS_PER_DAY);
    return { from: saturday, to: sunday };
}

export function hasDateFacetRefinement(refinements: { attributeId: string }[] | undefined): boolean {
    return Boolean(refinements?.some((r) => r.attributeId === DATE_FACET_ATTRIBUTE_ID));
}

export function hasActiveDateFacet(refines: string[]): boolean {
    const prefix = `${DATE_FACET_ATTRIBUTE_ID}=`;
    return refines.some((r) => r.startsWith(prefix));
}
